import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Layout,
  Table,
  Button,
  Modal,
  Form,
  Input,
  message,
  Tabs,
  Space,
  Card,
  Typography,
  Tag,
  Tooltip,
  Badge,
  Select,
  Divider
} from 'antd'
import {
  PlusOutlined,
  SafetyOutlined,
  UserOutlined,
  LockOutlined,
  UnlockOutlined,
  EyeOutlined,
  EyeInvisibleOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import { useAuth } from '../hooks/AuthContext'
import { useMembership } from '../hooks/useMembership'
import { usePermission } from '../hooks/usePermission'
import { PermissionGuard } from '../components/PermissionGuard'
import RoleManagementModal from '../components/RoleManagementModal'
import api from '../utils/api'
import { getRoles } from '../api/permission'
import {
  ROLES,
  ROLE_NAMES,
  ROLE_COLORS,
  ACCOUNT_STATUS_NAMES
} from '../utils/permissionUtils'
import { getPlanName } from '../constants/plans'
import Hint from '../components/common/Hint'

const { Text, Title } = Typography
const { Option } = Select

const Content = styled.div`
  padding: 20px;
  @media (max-width: 820px) {
    padding: 5px;
  }
`

const ResponsiveTable = styled(Table)`
  .ant-table-container {
    overflow-x: auto;
  }

  .ant-table-body {
    min-width: 1000px;
  }

  @media (max-width: 820px) {
    display: none;
  }
`

const MobileCardList = styled.div`
  display: none;
  @media (max-width: 820px) {
    display: block;
  }
`

const StyledCard = styled(Card)`
  margin-bottom: 16px;
  .ant-card-body {
    padding: 16px;
  }
`

const EmployeeInfo = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
`

const EmployeeName = styled(Text)`
  font-weight: bold;
  max-width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const AddEmployeeButton = styled(Button)`
  @media (max-width: 820px) {
    width: 100%;
    margin-top: 10px;
  }
`

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;

  @media (max-width: 820px) {
    flex-direction: column;
    align-items: stretch;
  }
`

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;

  @media (max-width: 820px) {
    flex-direction: column;
  }
`

const EmployeesPage = () => {
  const [employees, setEmployees] = useState([])
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [isRoleModalVisible, setIsRoleModalVisible] = useState(false)
  const [selectedEmployee, setSelectedEmployee] = useState(null)
  const [isDeletingEmployee, setIsDeletingEmployee] = useState(false)
  const [loading, setLoading] = useState(false)
  const [roles, setRoles] = useState([])

  const navigate = useNavigate()
  const { user, permissions, permissionsLoaded } = useAuth()
  const { getPlanLimit, plan: planKey } = useMembership()
  const employeeLimit = getPlanLimit('employees')
  const employeeCount = employees.length
  const reachedEmployeeLimit = employeeCount >= employeeLimit
  const { can, businessRole } = usePermission()

  // 方案顯示名稱統一由 src/constants/plans.js 提供
  // （與後端 shared/config/planLimits.js 的 PLAN_DISPLAY_NAME 對齊）
  const planDisplayName = getPlanName(planKey) || '目前方案'

  const businessId = user?.businessId
  const plan = user?.plan

  const fetchEmployees = useCallback(async () => {
    try {
      setLoading(true)
      const response = await api.get(`/api/employees/${businessId}`)
      // 確保 response.data 是數組
      const employeesData = Array.isArray(response.data) ? response.data : []
      setEmployees(employeesData)
    } catch (error) {
      if (error.response?.status === 403) {
        message.error('您沒有許可權檢視員工列表')
      } else {
        message.error('獲取員工列表失敗')
      }
      // 發生錯誤時設置為空數組
      setEmployees([])
    } finally {
      setLoading(false)
    }
  }, [businessId])

  const fetchRoles = useCallback(async () => {
    try {
      const response = await getRoles(businessId)
      if (response.data.success) {
        setRoles(response.data.data)
      }
    } catch (error) {
      message.error('獲取角色列表失敗')
    }
  }, [businessId])

  useEffect(() => {
    // 只有當使用者資料和許可權資料都載入完成後才檢查許可權
    if (user && permissions !== undefined && can('employee:view')) {
      fetchEmployees()
      fetchRoles()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, permissions, fetchEmployees, fetchRoles])

  const showAddEmployeeModal = () => {
    // 嚴格防護：僅允許具備 employee:create 的使用者開啟
    if (!can('employee:create')) {
      message.error('您沒有許可權新增員工')
      return
    }
    setIsModalVisible(true)
  }

  const handleCancel = () => {
    setIsModalVisible(false)
    setSelectedEmployee(null)
  }

  const handleFinish = async (values) => {
    try {
      // 嚴格防護：新增他人 or 編輯他人需檢查對應權限
      const isEditing = Boolean(selectedEmployee)
      if (isEditing) {
        const isEditingSelf =
          selectedEmployee?._id && user?.userId === selectedEmployee._id
        if (!isEditingSelf && !can('employee:edit')) {
          message.error('您沒有許可權編輯其他員工')
          return
        }
        if (
          isEditingSelf &&
          !can('employee:edit_own') &&
          !can('employee:edit')
        ) {
          message.error('您沒有許可權編輯自己的資料')
          return
        }
      } else {
        if (!can('employee:create')) {
          message.error('您沒有許可權新增員工')
          return
        }
      }
      if (selectedEmployee) {
        // 編輯員工
        await api.put(
          `/api/employees/${businessId}/${selectedEmployee._id}`,
          values
        )
        message.success('員工更新成功')
      } else {
        // 新增員工
        const employeeData = {
          username: values.username,
          email: values.email,
          phone: values.phone,
          name: values.name,
          employeeType:
            values.employeeType || ROLE_NAMES[values.businessRole] || '員工',
          businessRole: values.businessRole || 'employee'
        }

        await api.post(`/api/employees/${businessId}`, employeeData)
        message.success('員工新增成功')
      }
      fetchEmployees()
      setIsModalVisible(false)
      setSelectedEmployee(null)
    } catch (error) {
      if (error.response?.status === 403) {
        message.error('您沒有許可權執行此操作')
      } else {
        message.error(
          '操作失敗：' + (error.response?.data?.message || error.message)
        )
      }
    }
  }

  const handleRowClick = (record) => {
    // 嚴格防護：
    // 1) 具有 employee:view 可進入任何員工詳細
    // 2) 僅有 employee:edit_own 時，只能檢視/編輯自己的資料
    if (can('employee:view')) {
      navigate(`/employees/${businessId}/${record._id}`)
      return
    }
    if (
      can('employee:edit_own') &&
      user?.userId &&
      record?._id === user.userId
    ) {
      navigate(`/employees/${businessId}/${record._id}`)
      return
    }
    message.warning('您沒有許可權檢視此員工資料')
  }

  // 切換員工在前台是否顯示（眼睛開關）
  // owner 自帶 '*' 一律放行；其他角色需要 employee:edit_visibility
  const handleToggleVisibility = async (employee) => {
    if (!can('employee:edit_visibility')) {
      message.error('您沒有許可權變更員工的前台顯示狀態')
      return
    }
    const next = !employee.hiddenFromFrontend
    try {
      await api.patch(
        `/api/employees/${businessId}/${employee._id}/visibility`,
        { hiddenFromFrontend: next }
      )
      message.success(next ? '已將該員工於前台隱藏' : '已將該員工於前台顯示')
      // 樂觀更新清單，避免重新 fetch 全部
      setEmployees((list) =>
        list.map((e) =>
          e._id === employee._id ? { ...e, hiddenFromFrontend: next } : e
        )
      )
    } catch (error) {
      if (error.response?.status === 403) {
        message.error('您沒有許可權變更員工的前台顯示狀態')
      } else {
        message.error(error?.response?.data?.message || '變更前台顯示狀態失敗')
      }
    }
  }

  const handleDeleteEmployee = async (employeeId) => {
    try {
      // 嚴格防護：後端操作前再次校驗
      if (!can('employee:delete')) {
        message.error('您沒有許可權刪除員工')
        return
      }
      setIsDeletingEmployee(true)
      await api.delete(`/api/employees/${businessId}/${employeeId}`)
      message.success('員工已成功刪除')
      fetchEmployees()
    } catch (error) {
      if (error.response?.status === 403) {
        message.error('您沒有許可權刪除員工')
      } else {
        message.error('刪除員工失敗')
      }
    } finally {
      setIsDeletingEmployee(false)
    }
  }

  const showDeleteConfirm = (employee) => {
    if (employee.businessRole === 'owner') {
      message.warning('不能刪除商家擁有者')
      return
    }

    // 嚴格防護：無刪除權限直接阻止
    if (!can('employee:delete')) {
      message.error('您沒有許可權刪除此員工')
      return
    }

    Modal.confirm({
      title: '確定要刪除此員工嗎？',
      content: (
        <div>
          <p>員工：{employee.username || employee.name}</p>
          <p style={{ color: '#ff4d4f' }}>刪除後將無法恢復</p>
        </div>
      ),
      okText: '確定刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk() {
        handleDeleteEmployee(employee._id)
      }
    })
  }

  const handleManageRole = (employee) => {
    // 嚴格防護：僅允許具備 employee:manage_role 的角色
    if (!can('employee:manage_role')) {
      message.error('您沒有許可權管理此員工的角色')
      return
    }
    setSelectedEmployee(employee)
    setIsRoleModalVisible(true)
  }

  const handleRoleModalClose = () => {
    setIsRoleModalVisible(false)
    setSelectedEmployee(null)
  }

  const handleRoleUpdateSuccess = () => {
    fetchEmployees()
  }

  const getStatusBadge = (status, hiddenFromFrontend = false) => {
    // 前台隱藏：把「啟用」改成橘燈 + 「前台隱藏」字樣，視覺立刻區隔。
    // 若同時是 inactive/suspended，原狀態訊號比較強，沿用原本顯示。
    if (hiddenFromFrontend && (!status || status === 'active')) {
      return <Badge status='warning' text='啟用 · 前台隱藏' />
    }
    const statusConfig = {
      active: { status: 'success', text: '啟用' },
      inactive: { status: 'error', text: '停用' },
      suspended: { status: 'warning', text: '暫停' }
    }
    const config = statusConfig[status] || statusConfig.active
    return <Badge status={config.status} text={config.text} />
  }

  const formatEmployeeDisplayName = (name, username) => {
    const safeName = String(name || '').trim() || '未命名'
    const safeUsername = String(username || '').trim()
    return safeUsername ? `${safeName}(${safeUsername})` : safeName
  }

  const columns = [
    {
      title: '員工',
      dataIndex: 'username',
      key: 'username',
      render: (text, record) => (
        <Space>
          <UserOutlined />
          <EmployeeName title={text}>
            {formatEmployeeDisplayName(record.name, text)}
          </EmployeeName>
        </Space>
      ),
      width: '15%'
    },
    {
      title: '郵箱',
      dataIndex: 'email',
      key: 'email',
      width: '20%'
    },
    {
      title: '電話',
      dataIndex: 'phone',
      key: 'phone',
      width: '15%'
    },
    {
      title: '角色',
      dataIndex: 'businessRole',
      key: 'businessRole',
      render: (role) => {
        if (!role) return <Tag>未設定</Tag>
        return (
          <Tooltip title={ROLE_NAMES[role]}>
            <Tag color={ROLE_COLORS[role]}>{ROLE_NAMES[role]}</Tag>
          </Tooltip>
        )
      },
      width: '15%'
    },
    {
      title: '狀態',
      key: 'accountStatus',
      render: (_, record) =>
        getStatusBadge(record.accountStatus || 'active', record.hiddenFromFrontend),
      width: '12%'
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <ActionButtons onClick={(e) => e.stopPropagation()}>
          <PermissionGuard
            permissions={['employee:edit', 'employee:edit_own']}
            requireAll={false}
          >
            <Button
              type='link'
              size='small'
              onClick={() => handleRowClick(record)}
            >
              編輯
            </Button>
          </PermissionGuard>

          <PermissionGuard permission='employee:manage_role'>
            <Tooltip title='管理角色和許可權'>
              <Button
                type='link'
                size='small'
                icon={<SafetyOutlined />}
                onClick={() => handleManageRole(record)}
                disabled={
                  record.businessRole === 'owner' && businessRole !== 'owner'
                }
              >
                許可權
              </Button>
            </Tooltip>
          </PermissionGuard>

          <PermissionGuard permission='employee:edit_visibility'>
            <Tooltip
              title={
                record.hiddenFromFrontend
                  ? '目前在前台「隱藏」中。點此可重新讓客人看見。'
                  : '目前在前台「顯示」中。點此可從客人預約頁、設計師清單藏起來（不影響既有預約）。'
              }
            >
              <Button
                type='link'
                size='small'
                icon={
                  record.hiddenFromFrontend ? (
                    <EyeInvisibleOutlined />
                  ) : (
                    <EyeOutlined />
                  )
                }
                onClick={() => handleToggleVisibility(record)}
              >
                {record.hiddenFromFrontend ? '已隱藏' : '顯示中'}
              </Button>
            </Tooltip>
          </PermissionGuard>

          <PermissionGuard permission='employee:delete'>
            <Button
              type='link'
              size='small'
              danger
              onClick={() => showDeleteConfirm(record)}
              disabled={isDeletingEmployee || record.businessRole === 'owner'}
            >
              刪除
            </Button>
          </PermissionGuard>
        </ActionButtons>
      ),
      width: '25%'
    }
  ]

  const renderMobileCard = (employee) => (
    <StyledCard key={employee._id} onClick={() => handleRowClick(employee)}>
      <Space direction='vertical' style={{ width: '100%' }}>
        <EmployeeInfo>
          <Space>
            <UserOutlined />
            <EmployeeName title={employee.username}>
              {formatEmployeeDisplayName(employee.name, employee.username)}
            </EmployeeName>
          </Space>
          {employee.businessRole && (
            <Tag color={ROLE_COLORS[employee.businessRole]}>
              {ROLE_NAMES[employee.businessRole]}
            </Tag>
          )}
        </EmployeeInfo>

        <EmployeeInfo>
          <Text type='secondary'>郵箱：</Text>
          <Text>{employee.email}</Text>
        </EmployeeInfo>

        <EmployeeInfo>
          <Text type='secondary'>電話：</Text>
          <Text>{employee.phone}</Text>
        </EmployeeInfo>

        <EmployeeInfo>
          <Text type='secondary'>狀態：</Text>
          {getStatusBadge(
            employee.accountStatus || 'active',
            employee.hiddenFromFrontend
          )}
        </EmployeeInfo>

        <ActionButtons onClick={(e) => e.stopPropagation()}>
          <PermissionGuard
            permissions={['employee:edit', 'employee:edit_own']}
            requireAll={false}
          >
            <Button size='small' onClick={() => handleRowClick(employee)}>
              編輯資料
            </Button>
          </PermissionGuard>

          <PermissionGuard permission='employee:manage_role'>
            <Button
              size='small'
              icon={<SafetyOutlined />}
              onClick={() => handleManageRole(employee)}
              disabled={
                employee.businessRole === 'owner' && businessRole !== 'owner'
              }
            >
              管理許可權
            </Button>
          </PermissionGuard>

          <PermissionGuard permission='employee:edit_visibility'>
            <Button
              size='small'
              icon={
                employee.hiddenFromFrontend ? (
                  <EyeInvisibleOutlined />
                ) : (
                  <EyeOutlined />
                )
              }
              onClick={() => handleToggleVisibility(employee)}
            >
              {employee.hiddenFromFrontend ? '已從前台隱藏' : '前台顯示中'}
            </Button>
          </PermissionGuard>

          <PermissionGuard permission='employee:delete'>
            <Button
              size='small'
              danger
              onClick={() => showDeleteConfirm(employee)}
              disabled={isDeletingEmployee || employee.businessRole === 'owner'}
            >
              刪除
            </Button>
          </PermissionGuard>
        </ActionButtons>
      </Space>
    </StyledCard>
  )

  // 按狀態分組員工
  // 注意：「前台隱藏」與 accountStatus 是兩個獨立維度。
  // 一位員工可以同時是「在職」+「前台隱藏中」（這正是老闆暫不接客的典型情境）。
  const activeEmployees = Array.isArray(employees)
    ? employees.filter((e) => !e.accountStatus || e.accountStatus === 'active')
    : []
  const inactiveEmployees = Array.isArray(employees)
    ? employees.filter(
        (e) => e.accountStatus === 'inactive' || e.accountStatus === 'suspended'
      )
    : []
  const hiddenEmployees = Array.isArray(employees)
    ? employees.filter((e) => e.hiddenFromFrontend === true)
    : []

  const tabItems = [
    {
      key: '1',
      label: `所有員工 (${employees.length})`,
      children: (
        <>
          <ResponsiveTable
            columns={columns}
            dataSource={employees}
            rowKey='_id'
            loading={loading}
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
              style: { cursor: 'pointer' }
            })}
            scroll={{ x: 1000 }}
          />
          <MobileCardList>{employees.map(renderMobileCard)}</MobileCardList>
        </>
      )
    },
    {
      key: '2',
      label: `在職員工 (${activeEmployees.length})`,
      children: (
        <>
          <ResponsiveTable
            columns={columns}
            dataSource={activeEmployees}
            rowKey='_id'
            loading={loading}
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
              style: { cursor: 'pointer' }
            })}
            scroll={{ x: 1000 }}
          />
          <MobileCardList>
            {activeEmployees.map(renderMobileCard)}
          </MobileCardList>
        </>
      )
    },
    {
      key: '3',
      label: `離職/暫停 (${inactiveEmployees.length})`,
      children: (
        <>
          <ResponsiveTable
            columns={columns}
            dataSource={inactiveEmployees}
            rowKey='_id'
            loading={loading}
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
              style: { cursor: 'pointer' }
            })}
            scroll={{ x: 1000 }}
          />
          <MobileCardList>
            {inactiveEmployees.map(renderMobileCard)}
          </MobileCardList>
        </>
      )
    },
    {
      key: '4',
      label: `前台隱藏中 (${hiddenEmployees.length})`,
      children: (
        <>
          {hiddenEmployees.length > 0 && (
            <Hint
              tone='info'
              style={{ marginBottom: 12 }}
              title='這些員工帳號仍正常運作（可登入 B 端、可被指派服務、既有預約照常進行），只是目前在客人端的店家頁、預約頁、設計師清單看不到他們。點員工卡片上的眼睛按鈕可隨時切回顯示。'
            />
          )}
          <ResponsiveTable
            columns={columns}
            dataSource={hiddenEmployees}
            rowKey='_id'
            loading={loading}
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
              style: { cursor: 'pointer' }
            })}
            scroll={{ x: 1000 }}
          />
          <MobileCardList>
            {hiddenEmployees.map(renderMobileCard)}
          </MobileCardList>
        </>
      )
    }
  ]

  // 等待許可權資料載入完成
  if (!user || !permissionsLoaded) {
    return (
      <Layout>
        <Content>
          <Card loading>
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Text type='secondary'>正在載入資料...</Text>
            </div>
          </Card>
        </Content>
      </Layout>
    )
  }

  // 許可權載入完成後，檢查是否有檢視許可權
  if (!can('employee:view')) {
    // 如果有編輯自己資料的許可權，跳轉到自己的資料頁面
    if (can('employee:edit_own') && user?.userId) {
      return (
        <Layout>
          <Content>
            <Card>
              <Space
                direction='vertical'
                align='center'
                style={{ width: '100%', padding: '40px 0' }}
              >
                <UserOutlined style={{ fontSize: 64, color: '#1890ff' }} />
                <Title level={4} style={{ marginTop: 16 }}>
                  歡迎，{user.username || user.name}
                </Title>
                <Text type='secondary' style={{ marginBottom: 24 }}>
                  您目前的角色：
                  <Tag
                    color={ROLE_COLORS[businessRole]}
                    style={{ marginLeft: 8 }}
                  >
                    {ROLE_NAMES[businessRole] || '未設定'}
                  </Tag>
                </Text>

                <Space
                  direction='vertical'
                  size='large'
                  style={{ width: '100%', maxWidth: 400 }}
                >
                  <Button
                    type='primary'
                    size='large'
                    block
                    icon={<UserOutlined />}
                    onClick={() =>
                      navigate(`/employees/${businessId}/${user.userId}`)
                    }
                  >
                    檢視我的個人資料
                  </Button>

                  {can('booking:view_own') && (
                    <Button
                      size='large'
                      block
                      onClick={() => navigate('/appointments')}
                    >
                      檢視我的預約
                    </Button>
                  )}

                  <Hint
                    title='許可權說明'
                    tone='info'
                  >
                    {
                      <div>
                        <p>您目前沒有檢視所有員工列表的許可權。</p>
                        <p>如需更多許可權，請聯絡商家管理員。</p>
                      </div>
                    }
                  </Hint>
                </Space>
              </Space>
            </Card>
          </Content>
        </Layout>
      )
    }

    // 完全沒有許可權
    return (
      <Layout>
        <Content>
          <Card>
            <Space
              direction='vertical'
              align='center'
              style={{ width: '100%', padding: '40px 0' }}
            >
              <LockOutlined style={{ fontSize: 48, color: '#ff4d4f' }} />
              <Title level={4}>許可權不足</Title>
              <Text type='secondary'>您沒有許可權訪問此頁面</Text>
              <Text type='secondary' style={{ fontSize: 12 }}>
                當前角色：{ROLE_NAMES[businessRole] || '未設定'}
              </Text>
              <Button
                type='primary'
                style={{ marginTop: 24 }}
                onClick={() => navigate('/dashboard')}
              >
                返回首頁
              </Button>
            </Space>
          </Card>
        </Content>
      </Layout>
    )
  }

  return (
    <Layout>
      <Content>
        <TopBar data-tour='employees-intro'>
          <Space>
            <Text strong style={{ fontSize: 18 }}>
              員工管理
            </Text>
            {businessRole && (
              <Tag color={ROLE_COLORS[businessRole]}>
                {ROLE_NAMES[businessRole]}
              </Tag>
            )}
          </Space>

          <PermissionGuard permission='employee:create'>
            <Space direction='vertical' size={4} align='end'>
              <Space size={6} wrap>
                <Tag color={reachedEmployeeLimit ? 'red' : 'blue'}>
                  {planDisplayName}
                </Tag>
                <span
                  style={{
                    fontSize: 12,
                    color: reachedEmployeeLimit ? '#ff4d4f' : '#888'
                  }}
                >
                  員工數：{employeeCount} / {employeeLimit}
                  {reachedEmployeeLimit ? '（已達上限）' : ''}
                </span>
              </Space>
              <Tooltip
                title={
                  reachedEmployeeLimit
                    ? `${planDisplayName}員工上限為 ${employeeLimit} 位，請升級方案以新增更多員工`
                    : `${planDisplayName}可新增至 ${employeeLimit} 位員工`
                }
              >
                <AddEmployeeButton
                  type='primary'
                  icon={<PlusOutlined />}
                  onClick={showAddEmployeeModal}
                  disabled={reachedEmployeeLimit}
                  danger={reachedEmployeeLimit}
                >
                  {reachedEmployeeLimit ? '已達員工上限' : '新增員工'}
                </AddEmployeeButton>
              </Tooltip>
              {reachedEmployeeLimit && (
                <Button
                  type='link'
                  size='small'
                  style={{ padding: 0 }}
                  onClick={() => navigate('/settings/membership')}
                >
                  前往升級方案 →
                </Button>
              )}
            </Space>
          </PermissionGuard>
        </TopBar>

        <Tabs defaultActiveKey='1' items={tabItems} />
      </Content>

      {/* 新增/編輯員工彈窗 */}
      <Modal
        title={selectedEmployee ? '編輯員工' : '新增員工'}
        open={isModalVisible}
        footer={null}
        onCancel={handleCancel}
        width={600}
      >
        <Form
          layout='vertical'
          initialValues={selectedEmployee || { businessRole: 'employee' }}
          onFinish={handleFinish}
        >
          <Form.Item
            name='username'
            label='帳號'
            rules={[{ required: true, message: '請輸入帳號' }]}
          >
            <Input placeholder='請輸入帳號名稱' />
          </Form.Item>

          <Form.Item
            name='name'
            label='姓名'
            rules={[{ required: true, message: '請輸入姓名' }]}
          >
            <Input placeholder='請輸入員工姓名' />
          </Form.Item>

          <Form.Item
            name='email'
            label='Email'
            rules={[
              { required: true, message: '請輸入郵箱' },
              { type: 'email', message: '請輸入有效的郵箱地址' }
            ]}
          >
            <Input placeholder='請輸入電子郵件' />
          </Form.Item>

          <Form.Item name='phone' label='手機'>
            <Input placeholder='請輸入手機號碼' />
          </Form.Item>

          {/* 新增員工時選擇角色 */}
          {!selectedEmployee && (
            <>
              <Divider style={{ margin: '16px 0' }}>角色與許可權設定</Divider>

              <Form.Item
                name='businessRole'
                label='角色'
                rules={[{ required: true, message: '請選擇角色' }]}
                tooltip='選擇員工的角色將決定其可使用的功能'
              >
                <Select placeholder='請選擇角色' size='large'>
                  {roles
                    .filter(
                      (role) =>
                        // 過濾掉 owner 角色，manager 角色只有 owner 可以設定
                        role.role !== ROLES.OWNER &&
                        (role.role !== ROLES.MANAGER ||
                          businessRole === ROLES.OWNER)
                    )
                    .map((role) => (
                      <Option key={role.role} value={role.role}>
                        <Space>
                          <Tag color={ROLE_COLORS[role.role]}>{role.name}</Tag>
                          <span style={{ fontSize: 12, color: '#8c8c8c' }}>
                            {role.description}
                          </span>
                        </Space>
                      </Option>
                    ))}
                </Select>
              </Form.Item>

              <Form.Item
                name='employeeType'
                label='員工型別'
                tooltip='員工的職位型別，用於顯示和分類'
              >
                <Select
                  placeholder='請選擇員工型別（可選）'
                  allowClear
                  options={[
                    { value: '一般員工', label: '一般員工' },
                    { value: '設計師', label: '設計師' },
                    { value: '美甲師', label: '美甲師' },
                    { value: '美睫師', label: '美睫師' },
                    { value: '美容師', label: '美容師' },
                    { value: '美體師', label: '美體師' },
                    { value: '美妝師', label: '美妝師' },
                    { value: '醫師', label: '醫師' }
                  ]}
                />
              </Form.Item>
            </>
          )}

          <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
            <Space>
              <Button type='primary' htmlType='submit' size='large'>
                {selectedEmployee ? '儲存' : '新增員工'}
              </Button>
              <Button onClick={handleCancel} size='large'>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 角色管理彈窗 */}
      <RoleManagementModal
        visible={isRoleModalVisible}
        employee={selectedEmployee}
        businessId={businessId}
        currentUserRole={businessRole}
        onClose={handleRoleModalClose}
        onSuccess={handleRoleUpdateSuccess}
      />
    </Layout>
  )
}

export default EmployeesPage
